import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  AutocompleteInput,
  ArrayInput,
  SimpleFormIterator,
  Create,
  Show,
  SimpleShowLayout,
  ArrayField,
  SingleFieldList,
  ChipField,
  FunctionField,
  useRecordContext,
  TopToolbar,
  CreateButton,
  ExportButton
} from 'react-admin';
import { SchemaDiscoveryButton } from './SchemaDiscoveryButton';
import { SchemaPreview } from './SchemaPreview';
import { SchemaEditor } from './SchemaEditor';
import { useDataModelHierarchy } from '../../hooks/useDataModelHierarchy';

// Schema List Component
export const SchemaList = () => (
  <List
    actions={
      <TopToolbar>
        <CreateButton />
        <SchemaDiscoveryButton />
        <ExportButton />
      </TopToolbar>
    }
  >
    <Datagrid rowClick="edit">
      <TextField source="name" label="Name" />
      <TextField source="key" label="Key" />
      <TextField source="domain" label="Domain" />
      <TextField source="category" label="Category" />
      <BooleanField source="configuration.isCore" label="Core" />
      <TextField source="currentVersion" label="Version" />
      <FunctionField
        label="Used By Apps"
        render={record => record.usedByApplications?.length || 0}
      />
      <DateField source="updatedAt" label="Updated" />
    </Datagrid>
  </List>
);

// Schema Show Component
export const SchemaShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" label="Name" />
      <TextField source="key" label="Key" />
      <TextField source="description" label="Description" />
      <TextField source="domain" label="Domain" />
      <TextField source="category" label="Category" />
      <TextField source="currentVersion" label="Current Version" />
      
      <BooleanField source="configuration.isCore" label="Core Schema" />
      <BooleanField source="configuration.allowExtensions" label="Allow Extensions" />
      <BooleanField source="configuration.requiresSync" label="Requires Sync" />
      
      <ArrayField source="configuration.tags" label="Tags">
        <SingleFieldList>
          <ChipField source="$" />
        </SingleFieldList>
      </ArrayField>
      
      <ArrayField source="usedByApplications" label="Used By Applications">
        <SingleFieldList>
          <ChipField source="$" />
        </SingleFieldList>
      </ArrayField>
      
      <ArrayField source="extendedByApplications" label="Extended By Applications">
        <SingleFieldList>
          <ChipField source="$" />
        </SingleFieldList>
      </ArrayField>
      
      <SchemaPreview />
      
      <DateField source="createdAt" label="Created" />
      <DateField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

// Schema Edit Component - Enhanced with tabs
export const SchemaEdit = () => <SchemaEditor />;

// Schema Create Component
export const SchemaCreate = () => {
  const { domains } = useDataModelHierarchy();

  // Build domain choices from DataModel domains + defaults
  const domainChoices = React.useMemo(() => {
    const dataModelDomains = domains.map(d => ({ id: d.name.toLowerCase(), name: d.name }));
    const defaultDomains = [
      { id: 'commerce', name: 'Commerce' },
      { id: 'identity', name: 'Identity' },
      { id: 'location', name: 'Location' },
      { id: 'operations', name: 'Operations' },
      { id: 'framework', name: 'Framework' },
      { id: 'analytics', name: 'Analytics' },
      { id: 'general', name: 'General' },
    ];
    
    // Merge and deduplicate
    const seen = new Set();
    const merged = [...dataModelDomains, ...defaultDomains].filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });
    
    return merged.sort((a, b) => a.name.localeCompare(b.name));
  }, [domains]);

  return (
    <Create>
      <SimpleForm>
        <TextInput source="name" label="Name" required />
        <TextInput source="key" label="Key" required />
        <TextInput source="description" label="Description" multiline rows={3} />
        
        <AutocompleteInput
          source="domain"
          label="Domain"
          choices={domainChoices}
          helperText="Select from existing domains or type a new domain name"
          freeSolo
          defaultValue="general"
        />
      
      <SelectInput
        source="category"
        label="Category"
        choices={[
          { id: 'entity', name: 'Entity' },
          { id: 'event', name: 'Event' },
          { id: 'aggregate', name: 'Aggregate' },
          { id: 'value-object', name: 'Value Object' },
        ]}
        defaultValue="entity"
      />
      
      <BooleanInput source="configuration.isCore" label="Core Schema" defaultValue={false} />
      <BooleanInput source="configuration.allowExtensions" label="Allow Extensions" defaultValue={true} />
      <BooleanInput source="configuration.requiresSync" label="Requires Sync" defaultValue={false} />
      
      <ArrayInput source="configuration.tags" label="Tags">
        <SimpleFormIterator>
          <TextInput source="" label="Tag" />
        </SimpleFormIterator>
      </ArrayInput>
      
      <TextInput 
        source="jsonSchema" 
        label="JSON Schema" 
        multiline 
        rows={10}
        helperText="JSON Schema definition for this schema"
        style={{ fontFamily: 'monospace' }}
        defaultValue={JSON.stringify({
          "$schema": "http://json-schema.org/draft-07/schema#",
          "type": "object",
          "title": "New Schema",
          "description": "Schema definition",
          "properties": {},
          "required": [],
          "additionalProperties": false
        }, null, 2)}
      />
      </SimpleForm>
    </Create>
  );
};
